import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Tooltip } from '@sumtotal/react-foundation';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import {
  StyledCard,
  StyledCardActionArea,
  CardContentDiv,
  NotificationDot,
  IconContainer,
  IconDiv,
  ContentDiv,
  NameDiv,
  StyledSpan,
  DescriptionDiv,
  NotificationListItem,
  NotificationCardDivider,
  ColoredActivityName,
  StyledLink
} from './notificationStyles';
import useUserContext from '../../hooks/useUserContext';
import getLogger from '../../../utils/logger';
import { updateInappNotificationsReadStatus } from '../services/notificationService';
import authService from '../../../authentication/AuthorizeService';
import decodeHtmlEntities from '../../../utils/htmlHelper';

const NotificationCard = ({ notificationData, updateNotification, closeDrawer }) => {
  const { user } = useUserContext();
  const history = useHistory();
  const { t } = useTranslation(['TRANSLATIONS_RCORE_TOPNAV']);
  const [tenantKey, setTenantKey] = useState('');
  const { ActivityName, ActivityId, DueDate } = JSON.parse(notificationData.macroData);
  const formattedDate = DueDate ? new Date(DueDate).toLocaleDateString(user.culture) : undefined;
  const { Title, Description } = JSON.parse(notificationData.notificationMessage);
  const descriptionDecodedString = decodeHtmlEntities(Description);
  const description = descriptionDecodedString.replace(DueDate, formattedDate).split(ActivityName);
  const modifiedActivityName =
    ActivityName.length > 100 ? `${ActivityName.substring(0, 100)}...` : `${ActivityName}.`;
  const coloredActivityName = <ColoredActivityName>{modifiedActivityName}</ColoredActivityName>;
  const displayActivityName =
    ActivityName.length > 100 ? (
      <Tooltip title={ActivityName} arrow placement="bottom-start">
        {coloredActivityName}
      </Tooltip>
    ) : (
      <span>{coloredActivityName}</span>
    );
  const { isRead, time } = notificationData;

  useEffect(() => {
    (async () => {
      const userProfile = await authService.getUserProfile();
      setTenantKey(userProfile.tenant);
    })();
  }, []);

  const updateInappNotificationsStatus = (notificationId, isReadStatus, event) => {
    if (event) event.stopPropagation();
    updateInappNotificationsReadStatus(tenantKey, user.personPK, notificationId, isReadStatus)
      .then((results) => {
        if (results) {
          updateNotification(notificationId);
        }
        return null;
      })
      .catch((err) => {
        getLogger().error(err.response);
      });
  };

  const handleCardActionAreaClick = (notificationId, updateReadStatus, event) => {
    event.stopPropagation();
    if (updateReadStatus) updateInappNotificationsStatus(notificationId, true);
    closeDrawer();
    const url = `/learning/core/activitydetails/ViewActivityDetails?ActivityId=${ActivityId}&UserMode=0`;
    history.push(`/c/pillarRedirect?relyingParty=LM&url=${encodeURIComponent(url)}`);
  };

  const dot = isRead ? (
    <div />
  ) : (
    <Icon iconType="MaterialIcon" iconName="FiberManualRecord" size={0.5} />
  );

  return (
    <NotificationListItem>
      <StyledCard>
        <StyledCardActionArea
          onClick={(event) =>
            handleCardActionAreaClick(notificationData.notificationId, !isRead, event)
          }
        >
          <CardContentDiv>
            <NotificationDot>{dot}</NotificationDot>
            <IconContainer>
              <IconDiv>
                <Icon iconType="IcomoonIcon" iconName="learningactivity" size={1.25} />
              </IconDiv>
            </IconContainer>
            <ContentDiv>
              <NameDiv>
                {Title}
                <StyledSpan>{time}</StyledSpan>
              </NameDiv>
              <DescriptionDiv>
                <div>
                  {description[0] && description[0]}
                  {displayActivityName}
                </div>
              </DescriptionDiv>
            </ContentDiv>
          </CardContentDiv>
        </StyledCardActionArea>
        <StyledLink
          component="button"
          id={`button-${notificationData.notificationId}`}
          underline="hover"
          onClick={(event) =>
            updateInappNotificationsStatus(notificationData.notificationId, !isRead, event)
          }
        >
          {isRead
            ? t('RCORE_INAPP_NOTIFICATION_ACTION_MARK_AS_UNREAD')
            : t('INAPP_NOTIFICATION_MARK_AS_READ')}
        </StyledLink>
      </StyledCard>
      <NotificationCardDivider />
    </NotificationListItem>
  );
};
// PropType check
NotificationCard.propTypes = {
  /**
   * Notification Data
   */
  notificationData: PropTypes.shape({
    time: PropTypes.string,
    isRead: PropTypes.bool,
    data: PropTypes.string,
    macroData: PropTypes.string,
    notificationId: PropTypes.string,
    notificationMessage: PropTypes.string
  }),
  /**
   * Notification read status change callback
   */
  updateNotification: PropTypes.func,
  /**
   * Callback to close the drawer
   */
  closeDrawer: PropTypes.func
};
NotificationCard.defaultProps = {
  notificationData: undefined,
  updateNotification: undefined,
  closeDrawer: undefined
};
export default NotificationCard;
